/*
  This is used to get the saved sections list.
*/

import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Box, Divider, IconButton, Stack, Typography } from '@mui/material';
import CustomButton from 'components/Button/CustomButton';
import CloseIcon from 'components/Icons/closeIcon';
import selectors from 'store/selectors';
import actions from 'store/actions';
import DIALOGS from 'utils/dialogIds';
import useResponsive from 'utils/CustomHooks/useResponsive';
import DoneIcon from '@mui/icons-material/Done';
import {
  ContentWrapper,
  CustomModalWrapper,
  FooterWrapper,
  HeaderWrapper,
  SearchCnt,
} from './styles';
import SectionIcon from 'components/Icons/sectionIcon';
import SearchInput from 'components/Form/TextField/SearchField.cmp';
import useTemplateBuilder from 'utils/CustomHooks/templateBuilder/useTemplateBuilder';
import { IChecklistSection } from 'pages/TemplateDashboard/context';

// Saved Sections List View.
const SavedSectionsList = () => {
  const { getSavedSections, injectSectionInTemplate } = useTemplateBuilder();
  const { isMobileDeviceSm } = useResponsive();
  const dispatch = useDispatch();
  const [isSaving, setIsSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  const [savedSections, setSavedSections] = useState<IChecklistSection[]>([]);

  const dialogState = useSelector(selectors.getDialogState);
  const modalState = dialogState?.[DIALOGS.SAVED_SECTIONS_LIST];

  const { open, data } = modalState || {};
  const { onSuccessCB, checklistId, orders, metaData, preTigger } = data || {};

  const handleClose = () => {
    dispatch(actions.closeDialog(DIALOGS.SAVED_SECTIONS_LIST));
  };

  // This is used to get the saved sections list.
  const getSavedSectionsList = async () => {
    const response = await getSavedSections();

    setSavedSections(response);
  };

  useEffect(() => {
    getSavedSectionsList();
  }, []);

  // This is used to add the selected sections to the template.
  const addToTemplateHandler = async () => {
    let prevOrder = orders?.prev;
    let newOrder = 0;

    // This is used to update the selected sections.
    // Ordering the sections based on the order of the sections in the template.
    const updatedSelected = selected?.map((id, index) => {
      if (index === 0) {
        newOrder = orders?.next
          ? (prevOrder + orders?.next) / 2
          : prevOrder + 1;

        prevOrder = newOrder;

        return {
          sectionId: id,
          order: newOrder,
          ...(metaData ?? {}),
        };
      }
      newOrder = orders?.next ? (prevOrder + orders?.next) / 2 : prevOrder + 1;
      prevOrder = newOrder;
      return {
        sectionId: id,
        order: newOrder,
        ...(metaData ?? {}),
      };
    });

    // Save Template if it is not saved already - as it might impact the injection of the sections if template is not saved
    await preTigger?.();

    const payload = {
      targetChecklistId: checklistId,
      sectionOrders: updatedSelected,
    };

    // Call API to get the Section Details
    const response = await injectSectionInTemplate(payload);
    if (response) {
      onSuccessCB?.(response);
      handleClose();
    }
  };

  const handleChangeSearch = (value) => {
    setSearchTerm(value);
  };

  const filteredSections = savedSections?.filter(
    (item) =>
      !searchTerm ||
      item?.title?.toLowerCase()?.includes(searchTerm?.toLowerCase()),
  );

  // This is used to handle the section click.
  const handleSectionClick = (id) => {
    if (selected?.includes(id)) {
      setSelected(selected?.filter((itemId) => itemId !== id));
    } else {
      setSelected([...(selected ?? []), id]);
    }
  };

  return (
    <CustomModalWrapper
      smallScreen={isMobileDeviceSm}
      handleClose={handleClose}
      open={open}
      fullScreen={isMobileDeviceSm}
    >
      <HeaderWrapper>
        <Typography className="title">Saved Sections</Typography>
        <IconButton className={'closeIcon'} onClick={handleClose}>
          <CloseIcon sx={{ fontSize: '13px' }} />
        </IconButton>
      </HeaderWrapper>
      <SearchCnt>
        <SearchInput
          fieldProps={{
            value: searchTerm,
            onChange: (e) => handleChangeSearch(e.target.value),
          }}
          sx={{
            width: { sm: '100%', md: '100%' },
          }}
        />
      </SearchCnt>
      <ContentWrapper>
        {filteredSections?.map((item, index) => {
          const isSelected = selected?.includes(item?.id);
          return (
            <Box>
              <Stack
                className="item"
                direction={'row'}
                alignItems={'center'}
                justifyContent={'space-between'}
                gap="10px"
                onClick={() => handleSectionClick(item?.id)}
                sx={{
                  ...(isSelected && {
                    backgroundColor: 'rgba(235, 234, 255, 1)',
                  }),
                }}
              >
                <Stack direction="row" gap="10px" alignItems="center">
                  <Box
                    className="iconCnt"
                    sx={{
                      ...(isSelected && {
                        backgroundColor: 'rgba(104, 104, 254, 1) !important',
                        '& path': {
                          stroke: 'white !important',
                        },
                      }),
                    }}
                  >
                    <SectionIcon />
                  </Box>
                  <Typography className="sectionTitle">
                    {item?.title}
                  </Typography>
                </Stack>
                {isSelected && (
                  <DoneIcon
                    className="doneIcon"
                    style={{ height: 22, width: 22 }}
                  />
                )}
              </Stack>
              {index !== filteredSections?.length - 1 && <Divider />}
            </Box>
          );
        })}
      </ContentWrapper>

      <FooterWrapper direction="row" alignItems="center" justifyContent="end">
        <CustomButton
          variant={'text'}
          color={'primary'}
          onClick={handleClose}
          className="cancelBtn"
        >
          Cancel
        </CustomButton>

        <CustomButton
          variant={'contained'}
          disabled={selected?.length === 0}
          color={'primary'}
          onClick={addToTemplateHandler}
          buttonLoader={isSaving}
          className="saveBtn"
        >
          Add to Template
        </CustomButton>
      </FooterWrapper>
    </CustomModalWrapper>
  );
};

export default SavedSectionsList;
